import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { MonoLabel } from '../../Editorial';
import StatusChip, { chipState } from './StatusChip';

/* Two clips is what this section calls "enough film" (see the file comment
   below), so that is the target the chip grades against too. */
const FILM_TARGET = 2;

/* Up to two clips, because coaches watch before they read.
   Ported from `filmSection` in RecruitMeView.swift. */

const pretty = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function clipTitle(clip) {
  return clip.video_title
    || clip.label
    || (clip.video_meta && clip.video_meta.title)
    || pretty(clip.content_type)
    || 'Untitled clip';
}

function clipMeta(clip) {
  const parts = [];
  const platform = clip.video_meta && clip.video_meta.platform;
  if (platform) parts.push(pretty(platform));
  if (clip.context_label) parts.push(clip.context_label);
  if (parts.length === 0 && clip.category) parts.push(pretty(clip.category));
  return parts.join(' · ');
}

export default function FilmSection() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  // Clips whose thumbnail URL failed to load. They fall back to the same
  // placeholder a clip with no thumbnail gets, so the tile keeps its 16:9
  // shape — hiding the <img> instead collapses the tile to an empty area
  // stretched to its sibling's height.
  const [failedThumbs, setFailedThumbs] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/videos/me`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (!cancelled) setVideos(res.data || []); })
      .catch((err) => console.error('Error loading film for Recruit Me:', err));
    return () => { cancelled = true; };
  }, [API_URL]);

  const playable = videos.filter((v) => v.video_url);
  const clips = playable.slice(0, 2);

  return (
    <div className="me-group">
      <div className="me-eyebrow">
        <span>
          <MonoLabel>Film</MonoLabel>
          <StatusChip id="film" state={chipState(playable.length, FILM_TARGET)} />
        </span>
        <span className="me-eyebrow__hint">
          {playable.length > 0 && `${playable.length} clip${playable.length === 1 ? '' : 's'}`}
        </span>
        <button type="button" className="me-eyebrow__action" onClick={() => navigate('/videos')} data-testid="recruit_me_film_add">
          + Add
        </button>
      </div>

      {clips.length === 0 ? (
        <p className="me-empty-note">No film yet — coaches watch before they read.</p>
      ) : (
        <div className="me-film__tiles">
          {clips.map((clip) => {
            const thumb = clip.thumbnail_url || (clip.video_meta && clip.video_meta.thumbnail_url);
            const showThumb = thumb && !failedThumbs.has(clip.id);
            return (
              <button type="button" className="me-film__tile" key={clip.id} onClick={() => navigate('/videos')}>
                {showThumb
                  ? (
                    <img
                      className="me-film__thumb"
                      src={thumb}
                      alt=""
                      loading="lazy"
                      onError={() => setFailedThumbs((prev) => new Set(prev).add(clip.id))}
                    />
                  )
                  : <span className="me-film__thumb" data-testid="film_thumb_placeholder" aria-hidden="true" />}
                <span className="me-film__meta">
                  <span className="me-film__title">{clipTitle(clip)}</span>
                  <span className="me-film__sub">{clipMeta(clip)}</span>
                </span>
              </button>
            );
          })}
          {/* A lone clip must not stretch to full width — the pair layout is
              the design, so hold the second column open. */}
          {clips.length === 1 && <span className="me-film__spacer" aria-hidden="true" />}
        </div>
      )}
    </div>
  );
}
